import { FilterOperator } from "../../models";
import { StandardConstants } from "../standard";
import { ColorConstants } from "../styles";

export class ProjectIssuanceConstants {
  public static readonly ISSUANCE_STATUS_DRAFT = "Draft";

  public static readonly ISSUANCE_STATUS_INACTIVE = "Inactive";

  public static readonly ISSUANCE_STATUS_ISSUED = "Issued";

  public static readonly ISSUANCE_STATUS_SELECT_DATA = [
    { key: ProjectIssuanceConstants.ISSUANCE_STATUS_ISSUED, value: ProjectIssuanceConstants.ISSUANCE_STATUS_ISSUED },
    { key: ProjectIssuanceConstants.ISSUANCE_STATUS_DRAFT, value: ProjectIssuanceConstants.ISSUANCE_STATUS_DRAFT },
    {
      key: ProjectIssuanceConstants.ISSUANCE_STATUS_INACTIVE,
      value: ProjectIssuanceConstants.ISSUANCE_STATUS_INACTIVE,
    },
  ];

  /**
   * The Hub's default view of inventory: everything the organisation holds that is not retired.
   * Deliberately not the public default (Issued only) - see REQ-001/BR-02911.
   */
  public static readonly ISSUANCE_STATUS_HUB_DEFAULTS = [
    ProjectIssuanceConstants.ISSUANCE_STATUS_ISSUED,
    ProjectIssuanceConstants.ISSUANCE_STATUS_DRAFT,
  ];

  public static readonly ISSUANCE_UNIT_MEASUREMENT_CARBON = "Carbon";

  public static readonly ISSUANCE_UNIT_MEASUREMENT_BIODIVERSITY = "Biodiversity";

  public static readonly ISSUANCE_UNIT_MEASUREMENT_ECOSYSTEM_RECOVERY = "Ecosystem recovery";

  public static readonly ISSUANCE_UNIT_MEASUREMENT_NUTRIENT_NEUTRALITY = "Nutrient Neutrality";

  public static readonly ISSUANCE_UNIT_MEASUREMENT_RESTORATION = "30x30 Restoration Units";

  /**
   * Nutrient Neutrality quantities are an annual rate, so every quantity label on that measurement
   * carries this unit - grid headers and chart axis alike (SDD decision D-4).
   *
   * LOWERCASE, always. The Figma chart component reads "Kg/yr"; D-4 overrides it, and manual check
   * M-07 exists to catch a capitalised one. Referencing this constant everywhere is the defence.
   */
  public static readonly ISSUANCE_NUTRIENT_UNIT_LABEL = "kg/yr";

  /**
   * The dashboard inventory-breakdown tile palette: one family per unit measurement, from the design
   * (Dashboards | Seed - Hub, node 3206-4928).
   *
   * Deliberately NOT getInventoryKeyColours, which is the inventory charts' verified/pending scheme -
   * INDIGO for Carbon and GREEN for everything else. That has two families and would draw
   * Biodiversity and Nutrient Neutrality identically, which is the one thing this card exists to
   * avoid. The two mappings answer different questions and stay separate.
   *
   * A TS map rather than CSS classes (the BNGUnitsKPI approach) because the tile set is whatever the
   * organisation holds: a class name derived from an unmapped measurement would render the tile
   * unstyled and invisible, where the resolver below always returns a legible palette.
   */
  public static readonly UNIT_MEASUREMENT_TILE_COLOURS: {
    [key: string]: { dot: string; background: string; text: string };
  } = {
    [ProjectIssuanceConstants.ISSUANCE_UNIT_MEASUREMENT_CARBON]: {
      dot: ColorConstants.INDIGO_500,
      background: ColorConstants.INDIGO_50,
      text: ColorConstants.GREY_500,
    },
    [ProjectIssuanceConstants.ISSUANCE_UNIT_MEASUREMENT_BIODIVERSITY]: {
      dot: ColorConstants.GOLDENROD_500,
      background: ColorConstants.GOLDENROD_50,
      text: ColorConstants.GOLDENROD_800,
    },
    [ProjectIssuanceConstants.ISSUANCE_UNIT_MEASUREMENT_NUTRIENT_NEUTRALITY]: {
      dot: ColorConstants.SKY_BLUE_500,
      background: ColorConstants.SKY_BLUE_50,
      text: ColorConstants.SKY_BLUE_800,
    },
    // MAGENTA, the family Restoration (Restore 30x30) takes on every inventory chart (KAN-9953).
    [ProjectIssuanceConstants.ISSUANCE_UNIT_MEASUREMENT_RESTORATION]: {
      dot: ColorConstants.MAGENTA_500,
      background: ColorConstants.MAGENTA_50,
      text: ColorConstants.MAGENTA_800,
    },
  };

  /**
   * Any measurement the design does not name - Ecosystem recovery today, and whatever is added next.
   * A deliberate placeholder rather than an invented palette: the card must stay legible for a
   * measurement nobody has chosen a colour for.
   */
  public static readonly UNIT_MEASUREMENT_TILE_COLOURS_FALLBACK = {
    dot: ColorConstants.GREY_300,
    background: ColorConstants.GREY_25,
    text: ColorConstants.GREY_500,
  };

  /** Never returns undefined - see the fallback above. */
  public static getUnitMeasurementTileColours(unitMeasurement: string): {
    dot: string;
    background: string;
    text: string;
  } {
    return (
      ProjectIssuanceConstants.UNIT_MEASUREMENT_TILE_COLOURS[unitMeasurement] ??
      ProjectIssuanceConstants.UNIT_MEASUREMENT_TILE_COLOURS_FALLBACK
    );
  }

  public static readonly ISSUANCE_QUANTITY_TYPE_PIU = "PIU";

  public static readonly ISSUANCE_QUANTITY_TYPE_EIU = "EIU";

  public static readonly ISSUANCE_QUANTITY_TYPE_WCU = "WCU";

  public static readonly ISSUANCE_QUANTITY_TYPE_PCU = "PCU";

  public static readonly ISSUANCE_QUANTITY_TYPE_WCC = "WCC";

  public static readonly ISSUANCE_QUANTITY_TYPE_BY_STANDARD: { [key: string]: string[] } = {
    [StandardConstants.WOODLAND_CARBON_CODE_UUID]: ["PIU", "WCU"],
    [StandardConstants.PEATLAND_CODE_UUID]: ["PIU", "PCU"],
    [StandardConstants.WILDER_CARBON_UUID]: ["EIU", "WCC"],
  };

  public static readonly ISSUANCE_QUANTITY_TYPE_LONG_HAND: { [key: string]: string } = {
    [ProjectIssuanceConstants.ISSUANCE_QUANTITY_TYPE_PIU]: "Pending issuance units (PIUs)",
    [ProjectIssuanceConstants.ISSUANCE_QUANTITY_TYPE_EIU]: "Estimated issuance units (EIUs)",
    [ProjectIssuanceConstants.ISSUANCE_QUANTITY_TYPE_WCU]: "Woodland carbon units (WCUs)",
    [ProjectIssuanceConstants.ISSUANCE_QUANTITY_TYPE_PCU]: "Peatland carbon units (PCUs)",
    [ProjectIssuanceConstants.ISSUANCE_QUANTITY_TYPE_WCC]: "Wilder carbon credits (WCCs)",
  };

  public static readonly BNG_UNITKM_HABITAT_TYPES = ["Hedgerow", "Watercourse"];

  /**
   * Display labels for issuance fields whose key does not sentence-case into a usable label —
   * acronyms (lpa/nca/lnrs) and names that differ from the schema property (habitatType).
   * Shared by the issuance panel, the project inventory table and the inventory dashboards so a
   * field reads the same wherever it appears.
   */
  public static readonly ISSUANCE_FIELD_LABELS: Record<string, string> = {
    createdOrEnhanced: "Created or enhanced",
    designatedSite: "Designated site",
    eligibility: "Eligibility",
    habitatDistinctiveness: "Distinctiveness",
    habitatType: "Habitat group",
    lnrs: "Local nature recovery strategy area",
    lpa: "Local planning authority",
    nca: "National character area",
    quantityType: "Unit type",
    vintageEndDate: "Vintage end",
    vintageStartDate: "Vintage start",
    yearsEligible: "Eligibility term (yrs)",
  };

  public static readonly DEFAULT_BNG_ISSUANCE = {
    uuid: null,
    status: null,
    issuanceDate: null,
    quantityType: null,
    soldQuantity: 0,
    reservedQuantity: 0,
    availableQuantity: 0,
    quotedQuantity: 0,
    serialNumber: null,
    price: null,
    standardSpecificIssuanceData: {
      createdOrEnhanced: null,
      habitat: null,
      habitatType: null,
      habitatDistinctiveness: null,
      habitatCondition: null,
      lpa: null,
      nca: null,
      lnrs: null,
      unitHa: null,
      unitKm: null,
    },
    rowVersion: null,
    verifierUuid: null,
  };

  /*
    The blank Add overlay for a Nutrient Neutrality project. Every NN schema property the panel edits,
    all null, and nothing else: seeding it with the Carbon default gave an NN project a vintage pair
    and a verification status the NN schema does not declare, and none of its own fields.

    `unitMeasurement` and `quantityType` are deliberately absent from the blob - they are columns on
    the issuance row and are never written into it (decision D-2).
  */
  public static readonly DEFAULT_NN_ISSUANCE = {
    uuid: null,
    status: null,
    issuanceDate: null,
    quantityType: null,
    soldQuantity: 0,
    reservedQuantity: 0,
    availableQuantity: 0,
    quotedQuantity: 0,
    serialNumber: null,
    price: null,
    standardSpecificIssuanceData: {
      eligibility: null,
      yearsEligible: null,
      designatedSite: null,
      lpa: null,
      lnrs: null,
    },
    rowVersion: null,
    verifierUuid: null,
  };

  public static readonly DEFAULT_CARBON_ISSUANCE = {
    uuid: null,
    status: null,
    issuanceDate: null,
    quantityType: null,
    soldQuantity: 0,
    reservedQuantity: 0,
    availableQuantity: 0,
    quotedQuantity: 0,
    serialNumber: null,
    price: null,
    standardSpecificIssuanceData: {
      verificationStatus: null,
      vintageEndDate: null,
      vintageStartDate: null,
    },
    rowVersion: null,
    verifierUuid: null,
  };
}

/**
 * Selector label -> the issuance field each dimension view groups by. Only views whose label does
 * not already read as a heading, or whose group value can be empty, need an entry.
 *
 * No longer BNG-only: "By designated site" is a Nutrient Neutrality view, which is why this is named
 * for the inventory rather than for BNG.
 *
 * "By nutrient" is deliberately absent. It groups on the quantity_type COLUMN, which is never null
 * for an issuance, so that view must keep returning undefined from getInventoryEmptyGroupLabel -
 * exactly as "By project" and "By vintage" do.
 */
const INVENTORY_DIMENSION_VIEW_FIELD_KEYS: Record<string, string> = {
  "By LPA": "lpa",
  "By NCA": "nca",
  "By LNRS": "lnrs",
  "By designated site": "designatedSite",
};

/**
 * Dimension keys whose display label is an acronym, so "No LPA" is the correct rendering and
 * upper-casing is right. Anything else reads as a phrase and takes its spelled-out field label,
 * which is what keeps "By designated site" from rendering as "No DESIGNATEDSITE".
 */
const ACRONYM_DIMENSION_FIELD_KEYS = ["lpa", "nca", "lnrs"];

/**
 * The inventory view selector keeps its buttons compact with acronyms ("By NCA"), but the chart
 * heading above them spells the dimension out ("By national character area").
 */
export const getInventoryViewHeading = (selectorLabel: string): string => {
  const fieldKey = INVENTORY_DIMENSION_VIEW_FIELD_KEYS[selectorLabel];
  const fieldLabel = fieldKey ? ProjectIssuanceConstants.ISSUANCE_FIELD_LABELS[fieldKey] : undefined;

  return fieldLabel ? `By ${fieldLabel.toLowerCase()}` : selectorLabel;
};

/**
 * Several dimensions are optional on an issuance, so an aggregate view can return a group with no
 * value. Name that bucket after its dimension ("No NCA") instead of leaving the bar and the table
 * cell blank. Returns undefined for views whose group value is never empty.
 *
 * There is no server-side coalesce and there must not be one: the wire carries null, and this label
 * round-trips back to null through mapInventoryEmptyGroupFilterCriteria for the drill-down and the
 * grid filter. The acronym branch keeps the three shipped labels byte-identical.
 */
export const getInventoryEmptyGroupLabel = (selectorLabel: string): string | undefined => {
  const fieldKey = INVENTORY_DIMENSION_VIEW_FIELD_KEYS[selectorLabel];

  if (!fieldKey) return undefined;
  if (ACRONYM_DIMENSION_FIELD_KEYS.includes(fieldKey)) return `No ${fieldKey.toUpperCase()}`;

  const fieldLabel = ProjectIssuanceConstants.ISSUANCE_FIELD_LABELS[fieldKey];

  return `No ${(fieldLabel ?? fieldKey).toLowerCase()}`;
};

/** The negating string operators the data grid offers: "No LPA" under these means "every other group". */
const NEGATED_STRING_FILTER_OPERATORS: FilterOperator[] = ["neq", "ncontains"];

export interface InventoryGroupFilterCriteria {
  operator: FilterOperator;
  value: unknown;
}

/**
 * The group column renders an empty BNG dimension as its label ("No LPA"), but the value behind it is
 * null, so a filter typed against that label has to become a null comparison. Sending the label text
 * would ask the server for a group literally named "No LPA" and hide the very row being filtered for.
 * Criteria on any other value — or in a view whose group is never empty — are returned unchanged.
 */
export const mapInventoryEmptyGroupFilterCriteria = (
  groupFilterCriteria: InventoryGroupFilterCriteria | undefined,
  selectorLabel: string
): InventoryGroupFilterCriteria | undefined => {
  const emptyGroupLabel = getInventoryEmptyGroupLabel(selectorLabel);

  if (!groupFilterCriteria || !emptyGroupLabel || typeof groupFilterCriteria.value !== "string") {
    return groupFilterCriteria;
  }

  if (groupFilterCriteria.value.trim().toLowerCase() !== emptyGroupLabel.toLowerCase()) {
    return groupFilterCriteria;
  }

  return {
    operator: NEGATED_STRING_FILTER_OPERATORS.includes(groupFilterCriteria.operator) ? "neq" : "eq",
    value: null,
  };
};
